import React, { useEffect, useState } from 'react';
import {
    Grid, Typography, Accordion, AccordionSummary, AccordionDetails,
    Button, Box, Checkbox, FormControlLabel
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useProyecto } from '../../../data/ProyectoContext';
import { UseSetLenguajePorPais } from '../../../hooks/UseSetLenguajePorPais';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';


function ObjetivosAprendizaje(props) {
    const [dataOa, setDataOa] = useState([]);
    const [selectedIndicators, setSelectedIndicators] = useState({});
    const { materiaId, gradoId, setPromptData, objetivos, objetivosSeleccionados, setObjetivosSeleccionados } = props;
    const { proyecto, setProyecto } = useProyecto();
    const { t } = useTranslation(); // Hook de traducción

    //Hook para lenguaje según país
    UseSetLenguajePorPais();

    const handleIndicatorSelect = (objetivoCodigo, indicador) => {
        setSelectedIndicators((prev) => {
            const currentSelection = prev[objetivoCodigo] || [];
            if (currentSelection.includes(indicador)) {
                return {
                    ...prev,
                    [objetivoCodigo]: currentSelection.filter((item) => item !== indicador),
                };
            }
            return {
                ...prev,
                [objetivoCodigo]: [...currentSelection, indicador],
            };
        });
    };

    const handleAddObjectiveToProject = (objetivo) => {
        const selectedObjectiveIndicators = selectedIndicators[objetivo.codigo] || [];
        setProyecto((prevProyecto) => {
            const objetivoExistente = prevProyecto.objetivos_aprendizaje.find(
                (oa) => oa.codigo === objetivo.codigo
            );
            if (!objetivoExistente) {
                return {
                    ...prevProyecto,
                    objetivos_aprendizaje: [
                        ...prevProyecto.objetivos_aprendizaje,
                        {
                            codigo: objetivo.codigo,
                            objetivo: objetivo.objetivo,
                            indicadores: selectedObjectiveIndicators,
                            tipo: objetivo.tipo,
                            materia: Number(objetivo.materia),
                            grado: Number(objetivo.grado),
                        },
                    ],
                };
            }
            return prevProyecto;
        });

        setPromptData((prevData) => ({
            ...prevData,
            objetivos: [
                ...(prevData.objetivos || []),
                {
                    objetivo: objetivo.objetivo,
                    indicadores: selectedObjectiveIndicators,
                },
            ],
        }));

        setObjetivosSeleccionados((prevSeleccionados) => ({
            ...prevSeleccionados,
            [objetivo.codigo]: true,
        }));
    };

    const handleRemoveObjectiveFromProject = (objetivo) => {
        //Eliminar el objetivo de "objetivos_aprendizaje" en el estado del proyecto
        setProyecto((prevProyecto) => {
            const updatedObjetivos = prevProyecto.objetivos_aprendizaje.filter(
                (oa) => oa.codigo !== objetivo.codigo
            );
            return {
                ...prevProyecto,
                objetivos_aprendizaje: updatedObjetivos,
            };
        });

        //Actualizar "objetivosSeleccionados"
        setObjetivosSeleccionados((prevSeleccionados) => {
            const updatedSeleccionados = { ...prevSeleccionados };
            delete updatedSeleccionados[objetivo.codigo];
            return updatedSeleccionados;
        });

        //Eliminar los indicadores relacionados
        setSelectedIndicators((prev) => {
            const updatedIndicators = { ...prev };
            delete updatedIndicators[objetivo.codigo];
            return updatedIndicators;
        });

        //Actualizar "setPromptData" para reflejar el cambio en el backend
        setPromptData((prevData) => {
            const updatedObjetivos = prevData.objetivos.filter(
                (o) => o.objetivo !== objetivo.objetivo
            );
            return {
                ...prevData,
                objetivos: updatedObjetivos,
            };
        });

    };


    useEffect(() => {
        if (gradoId && materiaId) {
            const filteredObjectives = objetivos.filter(
                (objetivo) => objetivo.materia === materiaId && objetivo.grado === gradoId
            );
            setDataOa(filteredObjectives);
        }
    }, [materiaId, gradoId]);

    const groupedObjectives = dataOa.reduce((groups, objetivo) => {
        if (!groups[objetivo.tipo]) {
            groups[objetivo.tipo] = [];
        }
        groups[objetivo.tipo].push(objetivo);
        return groups;
    }, {});

    useEffect(() => {
        const initialSelectedIndicators = {};
        proyecto.objetivos_aprendizaje.forEach((oa) => {
            initialSelectedIndicators[oa.codigo] = oa.indicadores || [];
        });
        setSelectedIndicators(initialSelectedIndicators);
    }, [proyecto.objetivos_aprendizaje]);

    return (
        <Grid container spacing={4}>
            {Object.entries(groupedObjectives).map(([category, objectives]) => (
                <Grid item xs={12} key={category}>
                    <Typography sx={{ fontFamily: 'Montserrat', color: '#336B87', fontSize: '1rem' }}>{t('tipo')}</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#336B87' }}>
                        {category}
                    </Typography>
                    <Grid container spacing={3}>
                        {objectives.map((objetivo) => (
                            <Grid item xs={12} md={4} key={objetivo.codigo}>
                                <Accordion
                                    sx={{
                                        border: objetivosSeleccionados[objetivo.codigo]
                                            ? '2px solid #2B7C83'
                                            : '2px dashed #88A9B9',
                                        borderRadius: '8px',
                                        backgroundColor: objetivosSeleccionados[objetivo.codigo]
                                            ? '#EBF9EE'
                                            : 'rgba(0, 111, 149, 0.1)',
                                        '&:hover': {
                                            boxShadow: '0px 8px 10px rgba(0, 124, 119, 0.5)',
                                        },
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls="panel-content"
                                        id={`panel-${objetivo.codigo}-header`}
                                    >
                                        <Grid>
                                            <Typography sx={{ fontWeight: 'bold', color: '#336B87', fontSize: '1rem' }}>{objetivo.codigo}</Typography>
                                            <Grid margin={1}>

                                                <Typography sx={{ fontFamily: 'Montserrat', color: '#336B87', fontSize: '0.8rem', fontWeight: 'bold' }}>{t('objetivoAprendizaje')}</Typography>
                                                <Typography sx={{ fontFamily: 'Montserrat', color: '#336B87', fontSize: '0.8rem' }}>
                                                    {objetivo.objetivo}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Typography sx={{ fontFamily: 'Montserrat', color: '#336B87', fontSize: '1rem', fontWeight: 'bold', textAlign: 'center', mb: 2 }}>{t('indicadores')}</Typography>
                                        {objetivo.indicador.map((indicador, index) => (
                                            <FormControlLabel
                                                sx={{
                                                    '.MuiFormControlLabel-label': {
                                                        fontSize: '0.7rem',
                                                        color: '#2F2E41',
                                                        fontFamily: 'Montserrat'
                                                    },
                                                }}
                                                key={index}
                                                control={
                                                    <Checkbox
                                                        checked={selectedIndicators[objetivo.codigo]?.includes(indicador) || false}
                                                        onChange={() => handleIndicatorSelect(objetivo.codigo, indicador)}
                                                        disabled={
                                                            objetivosSeleccionados[objetivo.codigo] &&
                                                            !selectedIndicators[objetivo.codigo]?.includes(indicador)
                                                        }
                                                        sx={{
                                                            '& .MuiSvgIcon-root': {

                                                                width: '18px', // Tamaño del checkbox
                                                                height: '18px',
                                                            },
                                                            '&.Mui-checked': {
                                                                color: '#2B7C83', // Color del checkbox seleccionado
                                                            },
                                                        }}
                                                    />
                                                }
                                                label={indicador}
                                            />
                                        ))}
                                        <Box textAlign="center" mt={2}>
                                            <Button
                                                variant="contained"
                                                onClick={() => {
                                                    objetivosSeleccionados[objetivo.codigo]
                                                        ? handleRemoveObjectiveFromProject(objetivo)
                                                        : handleAddObjectiveToProject(objetivo);
                                                }}
                                                sx={{
                                                    mt: 2,
                                                    borderRadius: '20px',
                                                    backgroundColor: objetivosSeleccionados[objetivo.codigo] ? '#BA4534' : '#34C759',
                                                    '&:hover': {
                                                        backgroundColor: objetivosSeleccionados[objetivo.codigo] ? '#CD5C5C' : '#005559',
                                                    },
                                                }}
                                            >
                                                {objetivosSeleccionados[objetivo.codigo] ? 'Eliminar' : 'Agregar'}
                                            </Button>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            ))}
        </Grid>
    );
}



ObjetivosAprendizaje.propTypes = {
  materiaId: PropTypes.func.isRequired,
  gradoId: PropTypes.func.isRequired,
  setPromptData: PropTypes.func.isRequired,
  objetivos: PropTypes.func.isRequired,
  objetivosSeleccionados: PropTypes.func.isRequired,
  setObjetivosSeleccionados: PropTypes.func.isRequired,
};


export default ObjetivosAprendizaje;
